import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  Contractor,
  ContractorCoiExtractResponse,
  ContractorCoiLimits,
  ContractorLicense,
  ContractorPerson,
  EstimateWithScopes,
  UpdateContractorRequest,
} from '@bpd/shared';
import { formatCurrency, computeCoiStatus, computeLicenseStatus } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';

type LoadState = 'loading' | 'ready' | 'error' | 'not-configured';

function Sep() {
  return (
    <span aria-hidden="true" className="text-gray-300">
      |
    </span>
  );
}

interface ContractorEstimatesSectionProps {
  contractor: Contractor;
}

type EstimatesLoadState = 'loading' | 'ready' | 'error';

/** The caret-expanded body's estimate list — every quote this contractor has given, with every
 * project/task it applies to and a "View quote" link, so a quote like North Shores Painting's is
 * a 2-click lookup instead of hunting through project pages. Fetched fresh on every expand (a
 * lightweight scan-backed read — see listEstimates' contractorId branch — small enough for a
 * two-person admin tool that caching across expand/collapse isn't worth the complexity). */
function ContractorEstimatesSection({ contractor }: ContractorEstimatesSectionProps) {
  const [state, setState] = useState<EstimatesLoadState>('loading');
  const [estimates, setEstimates] = useState<EstimateWithScopes[]>([]);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);

  useEffect(() => {
    setState('loading');
    projectsApi
      .listEstimatesByContractor(contractor.id)
      .then((data) => {
        setEstimates(data);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, [contractor.id]);

  async function handleView(id: string) {
    setViewingId(id);
    setViewError(null);
    try {
      const { url } = await projectsApi.getEstimateDownloadUrl(id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setViewError(e instanceof Error ? e.message : 'Failed to open file');
    } finally {
      setViewingId(null);
    }
  }

  return (
    <div className="ml-6 mt-3 space-y-2 border-t border-gray-100 pt-3">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Estimates &amp; quotes</p>
      {state === 'loading' && <p className="text-sm text-gray-400">Loading…</p>}
      {state === 'error' && (
        <p className="text-sm text-red-600">Couldn&apos;t load estimates for this contractor.</p>
      )}
      {state === 'ready' && estimates.length === 0 && (
        <p className="text-sm text-gray-400">No estimates from this contractor yet.</p>
      )}
      {state === 'ready' &&
        estimates.map((e) => (
          <div key={e.id} className="rounded-md bg-gray-50 px-3 py-2 text-sm">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-medium text-gray-900">{e.title}</span>
              <span className="text-gray-600">
                {formatCurrency(e.totalAccepted)} accepted of {formatCurrency(e.totalPrice)}
              </span>
              {e.attachmentFileName && (
                <button
                  type="button"
                  onClick={() => void handleView(e.id)}
                  disabled={viewingId === e.id}
                  className="text-phoenix-blue-600 hover:underline"
                >
                  {viewingId === e.id ? 'Opening…' : 'View quote'}
                </button>
              )}
            </div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {e.scopes.length === 0 && (
                <span className="text-xs text-gray-400">Not attached to a project yet</span>
              )}
              {e.scopes.map((s) => (
                <Link
                  key={`${s.projectId}-${s.taskItemId ?? ''}`}
                  to={`/admin/projects/clients/${s.clientId}/${s.projectId}`}
                  state={{
                    from: 'contractor',
                    contractorId: contractor.id,
                    contractorName: contractor.name,
                    highlightEstimateId: e.id,
                  }}
                  title="View / edit this quote's details"
                  className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600 hover:border-phoenix-blue-300"
                >
                  {s.clientLabel} / {s.projectName}
                  {s.taskLabel ? ` (${s.taskLabel})` : ''}
                </Link>
              ))}
            </div>
          </div>
        ))}
      {viewError && <p className="text-xs text-red-600">{viewError}</p>}
    </div>
  );
}

interface ContractorPeopleSectionProps {
  contractor: Contractor;
  onUpdate: (updated: Contractor) => void;
}

/** The caret-expanded body of a contractor card — a contractor is often a company, and this is
 * where its individual people (owner, office manager, on-site foreman, etc.) live, each with their
 * own name/phone/email/notes. Managed as a whole-array replace on the contractor's `people` field
 * (see ContractorPerson's doc comment in shared types) rather than dedicated per-person routes. */
function ContractorPeopleSection({ contractor, onUpdate }: ContractorPeopleSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingPersonId, setEditingPersonId] = useState<string | null>(null);
  const [pName, setPName] = useState('');
  const [pPhone, setPPhone] = useState('');
  const [pEmail, setPEmail] = useState('');
  const [pNotes, setPNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const people = contractor.people ?? [];

  function resetForm() {
    setPName('');
    setPPhone('');
    setPEmail('');
    setPNotes('');
    setShowForm(false);
    setEditingPersonId(null);
    setError(null);
  }

  function startEdit(p: ContractorPerson) {
    setEditingPersonId(p.id);
    setPName(p.name);
    setPPhone(p.phone ?? '');
    setPEmail(p.email ?? '');
    setPNotes(p.notes ?? '');
    setShowForm(true);
  }

  async function savePeople(next: ContractorPerson[]) {
    setSaving(true);
    setError(null);
    try {
      const updated = await projectsApi.updateContractor(contractor.id, { people: next });
      onUpdate(updated);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!pName.trim()) return;
    const person: ContractorPerson = {
      id: editingPersonId ?? crypto.randomUUID(),
      name: pName.trim(),
      ...(pPhone ? { phone: pPhone } : {}),
      ...(pEmail ? { email: pEmail } : {}),
      ...(pNotes ? { notes: pNotes } : {}),
    };
    const next = editingPersonId
      ? people.map((p) => (p.id === editingPersonId ? person : p))
      : [...people, person];
    if (await savePeople(next)) resetForm();
  }

  async function handleRemove(id: string) {
    if (await savePeople(people.filter((p) => p.id !== id))) setConfirmRemoveId(null);
  }

  return (
    <div className="ml-6 mt-3 space-y-2 border-t border-gray-100 pt-3">
      {people.length === 0 && !showForm && (
        <p className="text-sm text-gray-400">No people added yet.</p>
      )}
      {people.map((p) => (
        <div
          key={p.id}
          className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md bg-gray-50 px-3 py-1.5 text-sm"
        >
          <span className="font-medium text-gray-900">{p.name}</span>
          {p.phone && (
            <>
              <Sep />
              <span className="text-gray-600">{p.phone}</span>
            </>
          )}
          {p.email && (
            <>
              <Sep />
              <span className="text-gray-600">{p.email}</span>
            </>
          )}
          {p.notes && (
            <>
              <Sep />
              <span className="text-gray-500">{p.notes}</span>
            </>
          )}
          <span className="ml-auto flex shrink-0 items-center gap-3">
            {confirmRemoveId === p.id ? (
              <>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void handleRemove(p.id)}
                  className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRemoveId(null)}
                  className="text-xs text-gray-500 hover:underline"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => startEdit(p)}
                  className="text-xs text-gray-600 hover:underline"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRemoveId(p.id)}
                  className="text-xs text-red-600 hover:underline"
                >
                  Remove
                </button>
              </>
            )}
          </span>
        </div>
      ))}

      {showForm ? (
        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="space-y-2 rounded-md border border-gray-200 bg-white p-3"
        >
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Name *"
              value={pName}
              onChange={(e) => setPName(e.target.value)}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
              maxLength={200}
              required
            />
            <input
              type="text"
              placeholder="Phone"
              value={pPhone}
              onChange={(e) => setPPhone(e.target.value)}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
            <input
              type="email"
              placeholder="Email"
              value={pEmail}
              onChange={(e) => setPEmail(e.target.value)}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
            <input
              type="text"
              placeholder="Notes"
              value={pNotes}
              onChange={(e) => setPNotes(e.target.value)}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-phoenix-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : editingPersonId ? 'Save changes' : 'Add person'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="text-sm text-gray-500 hover:underline"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="text-sm text-phoenix-blue-600 hover:underline"
        >
          + Add person
        </button>
      )}
      {error && !showForm && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

const LICENSE_STATUS_COLORS: Record<'green' | 'yellow' | 'red', string> = {
  green: 'bg-green-100 text-green-800',
  yellow: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
};

function LicenseStatusBadge({ expirationDate }: { expirationDate: string | undefined }) {
  const { status, reason } = computeLicenseStatus(expirationDate);
  if (status === 'none') return null;
  return (
    <span title={reason} className={`rounded-full px-2 py-0.5 text-xs font-medium ${LICENSE_STATUS_COLORS[status]}`}>
      {reason}
    </span>
  );
}

interface ContractorLicensesSectionProps {
  contractor: Contractor;
  onUpdate: (updated: Contractor) => void;
}

/** Licenses/certifications a contractor holds (e.g. a state asbestos-abatement license) — a list,
 * like ContractorPeopleSection, since a contractor can hold more than one. Fields are always typed
 * in by hand (no automated extraction like the CoI section — license layouts vary too much by trade
 * and issuing state to parse reliably), with an optional scanned copy attachable per-license once
 * the license row itself has been saved (its S3 key is scoped by licenseId, so it needs to exist in
 * the persisted array first — same reasoning as why CoI upload needs the contractor to already
 * exist). */
function ContractorLicensesSection({ contractor, onUpdate }: ContractorLicensesSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingLicenseId, setEditingLicenseId] = useState<string | null>(null);
  const [lNumber, setLNumber] = useState('');
  const [lType, setLType] = useState('');
  const [lIssuer, setLIssuer] = useState('');
  const [lExpiration, setLExpiration] = useState('');
  const [lNotes, setLNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [busyAttachmentId, setBusyAttachmentId] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  // The form's own attachment control (mirrors CoI: attach a file as part of the same add/edit
  // step, not a separate action after saving). `formLicenseId` is generated up front — even for a
  // brand-new license not yet saved — since the S3 key needs a stable licenseId and the upload-url
  // route deliberately doesn't require the license to already exist server-side (see index.ts).
  // `formAttachmentPersisted` tracks whether the current formAttachment is one already saved on the
  // Contractor (so Remove must call the real delete route) vs. a fresh upload not yet submitted (so
  // Remove just clears local state, leaving the orphaned S3 object — same tradeoff CoI's own
  // handleCancelReview already accepts).
  const [formLicenseId, setFormLicenseId] = useState<string | null>(null);
  const [formAttachment, setFormAttachment] = useState<{ key: string; fileName: string; contentType: string } | null>(
    null,
  );
  const [formAttachmentPersisted, setFormAttachmentPersisted] = useState(false);
  const [formAttachmentBusy, setFormAttachmentBusy] = useState(false);

  const licenses = contractor.licenses ?? [];

  function resetForm() {
    setLNumber('');
    setLType('');
    setLIssuer('');
    setLExpiration('');
    setLNotes('');
    setShowForm(false);
    setEditingLicenseId(null);
    setFormLicenseId(null);
    setFormAttachment(null);
    setFormAttachmentPersisted(false);
    setError(null);
  }

  function startAdd() {
    setEditingLicenseId(null);
    setLNumber('');
    setLType('');
    setLIssuer('');
    setLExpiration('');
    setLNotes('');
    setFormLicenseId(crypto.randomUUID());
    setFormAttachment(null);
    setFormAttachmentPersisted(false);
    setError(null);
    setShowForm(true);
  }

  function startEdit(l: ContractorLicense) {
    setEditingLicenseId(l.id);
    setLNumber(l.licenseNumber ?? '');
    setLType(l.licenseType ?? '');
    setLIssuer(l.issuer ?? '');
    setLExpiration(l.expirationDate ?? '');
    setLNotes(l.notes ?? '');
    setFormLicenseId(l.id);
    if (l.attachmentKey && l.attachmentFileName) {
      setFormAttachment({ key: l.attachmentKey, fileName: l.attachmentFileName, contentType: l.attachmentContentType ?? '' });
      setFormAttachmentPersisted(true);
    } else {
      setFormAttachment(null);
      setFormAttachmentPersisted(false);
    }
    setError(null);
    setShowForm(true);
  }

  async function saveLicenses(next: ContractorLicense[]) {
    setSaving(true);
    setError(null);
    try {
      const updated = await projectsApi.updateContractor(contractor.id, { licenses: next });
      onUpdate(updated);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!lType.trim() && !lNumber.trim()) return;
    const license: ContractorLicense = {
      id: editingLicenseId ?? formLicenseId ?? crypto.randomUUID(),
      ...(lNumber ? { licenseNumber: lNumber } : {}),
      ...(lType ? { licenseType: lType } : {}),
      ...(lIssuer ? { issuer: lIssuer } : {}),
      ...(lExpiration ? { expirationDate: lExpiration } : {}),
      ...(lNotes ? { notes: lNotes } : {}),
      ...(formAttachment
        ? {
            attachmentKey: formAttachment.key,
            attachmentFileName: formAttachment.fileName,
            attachmentContentType: formAttachment.contentType,
          }
        : {}),
    };
    const next = editingLicenseId
      ? licenses.map((l) => (l.id === editingLicenseId ? license : l))
      : [...licenses, license];
    if (await saveLicenses(next)) resetForm();
  }

  async function handleRemove(id: string) {
    if (await saveLicenses(licenses.filter((l) => l.id !== id))) setConfirmRemoveId(null);
  }

  async function handleFormFileChosen(file: File | null) {
    if (!file || !formLicenseId) return;
    setError(null);
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError('File is too large (max 15MB)');
      return;
    }
    const contentType = file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : '');
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(contentType)) {
      setError('Only PDF, JPG, or PNG files are supported');
      return;
    }
    setFormAttachmentBusy(true);
    try {
      const { uploadUrl, key } = await projectsApi.getContractorLicenseUploadUrl(
        contractor.id,
        formLicenseId,
        contentType,
        file.name,
      );
      const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
      if (!putRes.ok) throw new Error('Upload to storage failed');
      setFormAttachment({ key, fileName: file.name, contentType });
      setFormAttachmentPersisted(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to upload file');
    } finally {
      setFormAttachmentBusy(false);
    }
  }

  async function handleFormRemoveAttachment() {
    if (!formLicenseId) return;
    if (!formAttachmentPersisted) {
      // Never saved onto the Contractor yet — just clear local state (the just-uploaded S3 object
      // is left orphaned, same tradeoff as CoI's handleCancelReview).
      setFormAttachment(null);
      return;
    }
    setFormAttachmentBusy(true);
    setError(null);
    try {
      await projectsApi.deleteContractorLicenseAttachment(contractor.id, formLicenseId);
      setFormAttachment(null);
      setFormAttachmentPersisted(false);
      const next = licenses.map((l) => {
        if (l.id !== formLicenseId) return l;
        const { attachmentKey: _k, attachmentFileName: _f, attachmentContentType: _c, ...rest } = l;
        return rest;
      });
      onUpdate({ ...contractor, licenses: next });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove attachment');
    } finally {
      setFormAttachmentBusy(false);
    }
  }

  async function handleViewFormAttachment() {
    if (!formLicenseId) return;
    setViewingId(formLicenseId);
    setError(null);
    try {
      const { url } = await projectsApi.getContractorLicenseDownloadUrl(contractor.id, formLicenseId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to open file');
    } finally {
      setViewingId(null);
    }
  }

  // Row-level attach/replace/remove, for a license that's already saved — lets an existing license
  // pick up a file without opening the edit form (mirrors CoI's own inline Replace/Remove, which
  // works without clicking "Edit details" first). The add/edit form above additionally offers the
  // same action inline (via formLicenseId) so a *new* license can get its file in one step too.
  async function handleFileChosen(licenseId: string, file: File | null) {
    if (!file) return;
    setError(null);
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError('File is too large (max 15MB)');
      return;
    }
    const contentType = file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : '');
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(contentType)) {
      setError('Only PDF, JPG, or PNG files are supported');
      return;
    }
    setBusyAttachmentId(licenseId);
    try {
      const { uploadUrl, key } = await projectsApi.getContractorLicenseUploadUrl(
        contractor.id,
        licenseId,
        contentType,
        file.name,
      );
      const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
      if (!putRes.ok) throw new Error('Upload to storage failed');
      const next = licenses.map((l) =>
        l.id === licenseId
          ? { ...l, attachmentKey: key, attachmentFileName: file.name, attachmentContentType: contentType }
          : l,
      );
      await saveLicenses(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to upload file');
    } finally {
      setBusyAttachmentId(null);
    }
  }

  async function handleRemoveAttachment(licenseId: string) {
    setBusyAttachmentId(licenseId);
    setError(null);
    try {
      await projectsApi.deleteContractorLicenseAttachment(contractor.id, licenseId);
      const next = licenses.map((l) => {
        if (l.id !== licenseId) return l;
        const { attachmentKey: _k, attachmentFileName: _f, attachmentContentType: _c, ...rest } = l;
        return rest;
      });
      onUpdate({ ...contractor, licenses: next });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove attachment');
    } finally {
      setBusyAttachmentId(null);
    }
  }

  async function handleView(licenseId: string) {
    setViewingId(licenseId);
    setError(null);
    try {
      const { url } = await projectsApi.getContractorLicenseDownloadUrl(contractor.id, licenseId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to open file');
    } finally {
      setViewingId(null);
    }
  }

  return (
    <div className="ml-6 mt-3 space-y-2 border-t border-gray-100 pt-3">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Licenses</p>
      {licenses.length === 0 && !showForm && <p className="text-sm text-gray-400">No licenses added yet.</p>}
      {licenses.map((l) => (
        <div key={l.id} className="space-y-1.5 rounded-md bg-gray-50 px-3 py-1.5 text-sm">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium text-gray-900">{l.licenseType || 'License'}</span>
            {l.licenseNumber && (
              <>
                <Sep />
                <span className="text-gray-600">#{l.licenseNumber}</span>
              </>
            )}
            {l.issuer && (
              <>
                <Sep />
                <span className="text-gray-600">{l.issuer}</span>
              </>
            )}
            <LicenseStatusBadge expirationDate={l.expirationDate} />
            {l.notes && (
              <>
                <Sep />
                <span className="text-gray-500">{l.notes}</span>
              </>
            )}
            <span className="ml-auto flex shrink-0 items-center gap-3">
              {confirmRemoveId === l.id ? (
                <>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void handleRemove(l.id)}
                    className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    Confirm
                  </button>
                  <button type="button" onClick={() => setConfirmRemoveId(null)} className="text-xs text-gray-500 hover:underline">
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => startEdit(l)} className="text-xs text-gray-600 hover:underline">
                    Edit
                  </button>
                  <button type="button" onClick={() => setConfirmRemoveId(l.id)} className="text-xs text-red-600 hover:underline">
                    Remove
                  </button>
                </>
              )}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            {l.attachmentFileName ? (
              <>
                <span className="text-gray-500">{l.attachmentFileName}</span>
                <button
                  type="button"
                  onClick={() => void handleView(l.id)}
                  disabled={viewingId === l.id}
                  className="text-phoenix-blue-600 hover:underline"
                >
                  {viewingId === l.id ? 'Opening…' : 'View'}
                </button>
                <label className="cursor-pointer text-phoenix-blue-600 hover:underline">
                  Replace
                  <input
                    type="file"
                    accept={ATTACHMENT_ACCEPT}
                    className="hidden"
                    disabled={busyAttachmentId === l.id}
                    onChange={(e) => void handleFileChosen(l.id, e.target.files?.[0] ?? null)}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void handleRemoveAttachment(l.id)}
                  disabled={busyAttachmentId === l.id}
                  className="text-red-600 hover:underline"
                >
                  Remove file
                </button>
              </>
            ) : (
              <label className="cursor-pointer text-phoenix-blue-600 hover:underline">
                {busyAttachmentId === l.id ? 'Uploading…' : '+ Attach scanned copy'}
                <input
                  type="file"
                  accept={ATTACHMENT_ACCEPT}
                  className="hidden"
                  disabled={busyAttachmentId === l.id}
                  onChange={(e) => void handleFileChosen(l.id, e.target.files?.[0] ?? null)}
                />
              </label>
            )}
          </div>
        </div>
      ))}

      {showForm ? (
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-2 rounded-md border border-gray-200 bg-white p-3">
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="License type — e.g. Asbestos Abatement Contractor"
              value={lType}
              onChange={(e) => setLType(e.target.value)}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
              maxLength={200}
            />
            <input
              type="text"
              placeholder="License number"
              value={lNumber}
              onChange={(e) => setLNumber(e.target.value)}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
            <input
              type="text"
              placeholder="Issuer — e.g. State of Michigan"
              value={lIssuer}
              onChange={(e) => setLIssuer(e.target.value)}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
            <input
              type="text"
              placeholder="Expiration date (MM/DD/YYYY)"
              value={lExpiration}
              onChange={(e) => setLExpiration(e.target.value)}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
            <input
              type="text"
              placeholder="Notes"
              value={lNotes}
              onChange={(e) => setLNotes(e.target.value)}
              className="col-span-2 rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            {formAttachment ? (
              <>
                <span className="text-gray-500">{formAttachment.fileName}</span>
                <button
                  type="button"
                  onClick={() => void handleViewFormAttachment()}
                  disabled={viewingId === formLicenseId}
                  className="text-phoenix-blue-600 hover:underline"
                >
                  {viewingId === formLicenseId ? 'Opening…' : 'View'}
                </button>
                <label className="cursor-pointer text-phoenix-blue-600 hover:underline">
                  Replace
                  <input
                    type="file"
                    accept={ATTACHMENT_ACCEPT}
                    className="hidden"
                    disabled={formAttachmentBusy}
                    onChange={(e) => void handleFormFileChosen(e.target.files?.[0] ?? null)}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void handleFormRemoveAttachment()}
                  disabled={formAttachmentBusy}
                  className="text-red-600 hover:underline"
                >
                  Remove file
                </button>
              </>
            ) : (
              <label className="cursor-pointer text-phoenix-blue-600 hover:underline">
                {formAttachmentBusy ? 'Uploading…' : '+ Attach scanned copy (PDF, JPG, or PNG)'}
                <input
                  type="file"
                  accept={ATTACHMENT_ACCEPT}
                  className="hidden"
                  disabled={formAttachmentBusy}
                  onChange={(e) => void handleFormFileChosen(e.target.files?.[0] ?? null)}
                />
              </label>
            )}
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-phoenix-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : editingLicenseId ? 'Save changes' : 'Add license'}
            </button>
            <button type="button" onClick={resetForm} className="text-sm text-gray-500 hover:underline">
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button type="button" onClick={startAdd} className="text-sm text-phoenix-blue-600 hover:underline">
          + Add license
        </button>
      )}
      {error && !showForm && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024; // 15MB — generous for a scanned/photographed certificate

/** Green (>90 days left) / yellow (<=90 days) / red (<=30 days or expired) traffic light, shown
 * right on the collapsed card so an about-to-lapse CoI is visible without expanding anything.
 * Omitted entirely when there's no CoI on file yet (computeCoiStatus's 'none') to avoid clutter for
 * contractors nobody's gotten around to uploading one for. */
function CoiStatusBadge({ contractor }: { contractor: Contractor }) {
  const { status, reason } = computeCoiStatus(contractor.coiExpirationDate);
  if (status === 'none') return null;
  const colors: Record<'green' | 'yellow' | 'red', string> = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
  };
  return (
    <span
      title={reason}
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${colors[status]}`}
    >
      CoI: {status === 'green' ? 'Valid' : status === 'yellow' ? 'Expiring soon' : 'Expired/expiring'}
    </span>
  );
}

/** Same traffic-light treatment as CoiStatusBadge, but for whichever of the contractor's licenses
 * expires soonest — so an about-to-lapse license (e.g. an asbestos abatement license) is visible on
 * the collapsed card too, not just after expanding into the Licenses section. Omitted when there
 * are no licenses with an expiration date on file at all. */
function SoonestLicenseBadge({ contractor }: { contractor: Contractor }) {
  const licenses = contractor.licenses ?? [];
  const withDates = licenses.filter((l) => l.expirationDate);
  if (withDates.length === 0) return null;
  const soonest = withDates.reduce((a, b) => {
    const da = new Date(a.expirationDate!).getTime();
    const db = new Date(b.expirationDate!).getTime();
    return db < da ? b : a;
  });
  const { status, reason } = computeLicenseStatus(soonest.expirationDate);
  if (status === 'none') return null;
  return (
    <span title={reason} className={`rounded-full px-2 py-0.5 text-xs font-medium ${LICENSE_STATUS_COLORS[status]}`}>
      {soonest.licenseType || 'License'}: {status === 'green' ? 'Valid' : status === 'yellow' ? 'Expiring soon' : 'Expired/expiring'}
    </span>
  );
}

interface CoiFormState {
  insurerName: string;
  policyNumber: string;
  effectiveDate: string;
  expirationDate: string;
  eachOccurrence: string;
  damageToRentedPremises: string;
  medExp: string;
  personalAdvInjury: string;
  generalAggregate: string;
  productsCompOpAgg: string;
}

function emptyCoiForm(): CoiFormState {
  return {
    insurerName: '',
    policyNumber: '',
    effectiveDate: '',
    expirationDate: '',
    eachOccurrence: '',
    damageToRentedPremises: '',
    medExp: '',
    personalAdvInjury: '',
    generalAggregate: '',
    productsCompOpAgg: '',
  };
}

function coiFormFromContractor(c: Contractor): CoiFormState {
  return {
    insurerName: c.coiInsurerName ?? '',
    policyNumber: c.coiPolicyNumber ?? '',
    effectiveDate: c.coiEffectiveDate ?? '',
    expirationDate: c.coiExpirationDate ?? '',
    eachOccurrence: c.coiLimits?.eachOccurrence ?? '',
    damageToRentedPremises: c.coiLimits?.damageToRentedPremises ?? '',
    medExp: c.coiLimits?.medExp ?? '',
    personalAdvInjury: c.coiLimits?.personalAdvInjury ?? '',
    generalAggregate: c.coiLimits?.generalAggregate ?? '',
    productsCompOpAgg: c.coiLimits?.productsCompOpAgg ?? '',
  };
}

function coiFormFromExtract(r: ContractorCoiExtractResponse): CoiFormState {
  return {
    insurerName: r.insurerName ?? '',
    policyNumber: r.policyNumber ?? '',
    effectiveDate: r.effectiveDate ?? '',
    expirationDate: r.expirationDate ?? '',
    eachOccurrence: r.limits?.eachOccurrence ?? '',
    damageToRentedPremises: r.limits?.damageToRentedPremises ?? '',
    medExp: r.limits?.medExp ?? '',
    personalAdvInjury: r.limits?.personalAdvInjury ?? '',
    generalAggregate: r.limits?.generalAggregate ?? '',
    productsCompOpAgg: r.limits?.productsCompOpAgg ?? '',
  };
}

const CONFIDENCE_NOTES: Record<ContractorCoiExtractResponse['matchConfidence'], string | null> = {
  high: null,
  partial: 'Automated read only found some fields with confidence — please check every field below before saving.',
  low: 'Automated read had low confidence — most fields below are probably wrong or blank; check carefully.',
  unrecognized:
    "Couldn't automatically read this file's layout — enter the fields below by hand.",
};

const OCR_NOTE =
  'This looks like a scanned/faxed document with no selectable text, so it was read with OCR instead of a direct text scan — OCR is less reliable, so double-check every field below against the actual file.';

interface ContractorCoiSectionProps {
  contractor: Contractor;
  onUpdate: (updated: Contractor) => void;
}

/** Upload/view/replace/remove the contractor's Certificate of Insurance (ACORD 25 General
 * Liability), same direct-to-S3 presigned-PUT pattern as ProjectEstimatesSection's quote
 * AttachmentSection. The twist here: after upload, the server reads the PDF (coiParser.ts, no AI
 * per document — see scripts/parse_coi.py for the design rationale) and this shows an editable
 * review form pre-filled with what it found, so the admin confirms/corrects before anything is
 * saved onto the Contractor — the extract call itself never writes to the row. */
function ContractorCoiSection({ contractor, onUpdate }: ContractorCoiSectionProps) {
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [form, setForm] = useState<CoiFormState>(emptyCoiForm());
  const [confidence, setConfidence] = useState<ContractorCoiExtractResponse['matchConfidence'] | null>(null);
  const [ocrUsed, setOcrUsed] = useState(false);
  // Set only while reviewing a freshly-uploaded file not yet saved to the Contractor.
  const [pendingUpload, setPendingUpload] = useState<{ key: string; fileName: string; contentType: string } | null>(
    null,
  );

  async function handleFileChosen(file: File | null) {
    if (!file) return;
    setError(null);
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError('File is too large (max 15MB)');
      return;
    }
    const contentType = file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : '');
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(contentType)) {
      setError('Only PDF, JPG, or PNG files are supported');
      return;
    }
    setBusy(true);
    setBusyLabel('Uploading…');
    try {
      const { uploadUrl, key } = await projectsApi.getContractorCoiUploadUrl(contractor.id, contentType, file.name);
      const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
      if (!putRes.ok) throw new Error('Upload to storage failed');
      setBusyLabel('Reading certificate…');
      const extracted = await projectsApi.extractContractorCoi(contractor.id, key);
      setPendingUpload({ key, fileName: file.name, contentType });
      setConfidence(extracted.matchConfidence);
      setOcrUsed(Boolean(extracted.ocrUsed));
      setForm(coiFormFromExtract(extracted));
      setReviewing(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to upload/read certificate');
    } finally {
      setBusy(false);
      setBusyLabel('');
    }
  }

  function startEditExisting() {
    setPendingUpload(null);
    setConfidence(null);
    setOcrUsed(false);
    setForm(coiFormFromContractor(contractor));
    setError(null);
    setReviewing(true);
  }

  async function handleSaveReview() {
    setBusy(true);
    setError(null);
    try {
      const limits: ContractorCoiLimits = {
        ...(form.eachOccurrence ? { eachOccurrence: form.eachOccurrence } : {}),
        ...(form.damageToRentedPremises ? { damageToRentedPremises: form.damageToRentedPremises } : {}),
        ...(form.medExp ? { medExp: form.medExp } : {}),
        ...(form.personalAdvInjury ? { personalAdvInjury: form.personalAdvInjury } : {}),
        ...(form.generalAggregate ? { generalAggregate: form.generalAggregate } : {}),
        ...(form.productsCompOpAgg ? { productsCompOpAgg: form.productsCompOpAgg } : {}),
      };
      const payload: UpdateContractorRequest = {
        ...(form.insurerName ? { coiInsurerName: form.insurerName } : {}),
        ...(form.policyNumber ? { coiPolicyNumber: form.policyNumber } : {}),
        ...(form.effectiveDate ? { coiEffectiveDate: form.effectiveDate } : {}),
        ...(form.expirationDate ? { coiExpirationDate: form.expirationDate } : {}),
        coiLimits: limits,
        ...(confidence ? { coiExtractionConfidence: confidence } : {}),
        ...(pendingUpload
          ? {
              coiAttachmentKey: pendingUpload.key,
              coiAttachmentFileName: pendingUpload.fileName,
              coiAttachmentContentType: pendingUpload.contentType,
            }
          : {}),
      };
      const updated = await projectsApi.updateContractor(contractor.id, payload);
      onUpdate(updated);
      setReviewing(false);
      setPendingUpload(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  function handleCancelReview() {
    setReviewing(false);
    setPendingUpload(null);
    setError(null);
    // A freshly-uploaded-but-cancelled file's S3 object is left orphaned rather than deleted here —
    // same tradeoff already accepted by ProjectEstimatesSection's attachment flow.
  }

  async function handleView() {
    setViewing(true);
    setError(null);
    try {
      const { url } = await projectsApi.getContractorCoiDownloadUrl(contractor.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to open file');
    } finally {
      setViewing(false);
    }
  }

  async function handleRemove() {
    setBusy(true);
    setError(null);
    try {
      await projectsApi.deleteContractorCoiAttachment(contractor.id);
      const {
        coiAttachmentKey: _k,
        coiAttachmentFileName: _f,
        coiAttachmentContentType: _c,
        coiInsurerName: _in,
        coiPolicyNumber: _pn,
        coiEffectiveDate: _ed,
        coiExpirationDate: _xd,
        coiLimits: _l,
        coiExtractionConfidence: _cc,
        ...rest
      } = contractor;
      onUpdate(rest);
      setConfirmRemove(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove');
    } finally {
      setBusy(false);
    }
  }

  const { status, reason } = computeCoiStatus(contractor.coiExpirationDate);

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Certificate of insurance</p>

      {reviewing ? (
        <div className="mt-2 space-y-2 rounded-md border border-gray-200 bg-white p-3">
          {ocrUsed && (
            <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800">{OCR_NOTE}</p>
          )}
          {CONFIDENCE_NOTES[confidence ?? 'high'] && confidence && (
            <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
              {CONFIDENCE_NOTES[confidence]}
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Insurer name"
              value={form.insurerName}
              onChange={(e) => setForm({ ...form, insurerName: e.target.value })}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
            <input
              type="text"
              placeholder="Policy number"
              value={form.policyNumber}
              onChange={(e) => setForm({ ...form, policyNumber: e.target.value })}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
            <input
              type="text"
              placeholder="Effective date (MM/DD/YYYY)"
              value={form.effectiveDate}
              onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
            <input
              type="text"
              placeholder="Expiration date (MM/DD/YYYY)"
              value={form.expirationDate}
              onChange={(e) => setForm({ ...form, expirationDate: e.target.value })}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
          </div>
          <p className="pt-1 text-xs font-medium uppercase tracking-wide text-gray-400">
            General liability limits
          </p>
          <div className="grid grid-cols-3 gap-2">
            <input
              type="text"
              placeholder="Each occurrence"
              value={form.eachOccurrence}
              onChange={(e) => setForm({ ...form, eachOccurrence: e.target.value })}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
            <input
              type="text"
              placeholder="Damage to rented premises"
              value={form.damageToRentedPremises}
              onChange={(e) => setForm({ ...form, damageToRentedPremises: e.target.value })}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
            <input
              type="text"
              placeholder="Med exp"
              value={form.medExp}
              onChange={(e) => setForm({ ...form, medExp: e.target.value })}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
            <input
              type="text"
              placeholder="Personal & adv injury"
              value={form.personalAdvInjury}
              onChange={(e) => setForm({ ...form, personalAdvInjury: e.target.value })}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
            <input
              type="text"
              placeholder="General aggregate"
              value={form.generalAggregate}
              onChange={(e) => setForm({ ...form, generalAggregate: e.target.value })}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
            <input
              type="text"
              placeholder="Products-comp/op agg"
              value={form.productsCompOpAgg}
              onChange={(e) => setForm({ ...form, productsCompOpAgg: e.target.value })}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-sm"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={() => void handleSaveReview()}
              disabled={busy}
              className="rounded-md bg-phoenix-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={handleCancelReview} className="text-sm text-gray-500 hover:underline">
              Cancel
            </button>
          </div>
        </div>
      ) : contractor.coiAttachmentFileName ? (
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <span className="text-sm text-gray-700">{contractor.coiAttachmentFileName}</span>
          {status !== 'none' && (
            <span
              title={reason}
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                status === 'green'
                  ? 'bg-green-100 text-green-800'
                  : status === 'yellow'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-red-100 text-red-800'
              }`}
            >
              {reason}
            </span>
          )}
          <button type="button" onClick={() => void handleView()} disabled={viewing} className="text-sm text-phoenix-blue-600 hover:underline">
            {viewing ? 'Opening…' : 'View'}
          </button>
          <button type="button" onClick={startEditExisting} className="text-sm text-phoenix-blue-600 hover:underline">
            Edit details
          </button>
          <label className="cursor-pointer text-sm text-phoenix-blue-600 hover:underline">
            Replace
            <input
              type="file"
              accept={ATTACHMENT_ACCEPT}
              className="hidden"
              disabled={busy}
              onChange={(e) => void handleFileChosen(e.target.files?.[0] ?? null)}
            />
          </label>
          {!confirmRemove ? (
            <button type="button" onClick={() => setConfirmRemove(true)} className="text-sm text-red-600 hover:underline">
              Remove
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => void handleRemove()}
                disabled={busy}
                className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-red-700"
              >
                Confirm
              </button>
              <button type="button" onClick={() => setConfirmRemove(false)} className="text-sm text-gray-500 hover:underline">
                Cancel
              </button>
            </>
          )}
        </div>
      ) : (
        <label className="mt-1 inline-block cursor-pointer text-sm text-phoenix-blue-600 hover:underline">
          {busy ? busyLabel || 'Working…' : '+ Upload Certificate of Insurance (PDF, JPG, or PNG)'}
          <input
            type="file"
            accept={ATTACHMENT_ACCEPT}
            className="hidden"
            disabled={busy}
            onChange={(e) => void handleFileChosen(e.target.files?.[0] ?? null)}
          />
        </label>
      )}
      {!reviewing && error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

export function ContractorsPage() {
  const [state, setState] = useState<LoadState>('loading');
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [trade, setTrade] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterQuery, setFilterQuery] = useState('');

  function load() {
    if (!projectsApi.isConfigured()) {
      setState('not-configured');
      return;
    }
    setState('loading');
    projectsApi
      .listContractors()
      .then((data) => {
        setContractors(data);
        setState('ready');
      })
      .catch(() => setState('error'));
  }

  useEffect(load, []);

  const filteredContractors = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return contractors;
    return contractors.filter((c) =>
      [c.name, c.trade, c.phone].some((f) => f && f.toLowerCase().includes(q)),
    );
  }, [contractors, filterQuery]);

  function resetForm() {
    setName('');
    setPhone('');
    setTrade('');
    setNotes('');
    setShowAdd(false);
    setEditingId(null);
    setError(null);
  }

  function startAdd() {
    setEditingId(null);
    setName('');
    setPhone('');
    setTrade('');
    setNotes('');
    setError(null);
    setShowAdd(true);
  }

  /** Edit happens in-place inside the card's caret-expanded body (next to the people section)
   * rather than in the top-of-page form, so it's not scrolled miles away from what's being edited. */
  function startEdit(c: Contractor) {
    setShowAdd(false);
    setEditingId(c.id);
    setExpandedId(c.id);
    setName(c.name);
    setPhone(c.phone ?? '');
    setTrade(c.trade ?? '');
    setNotes(c.notes ?? '');
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        ...(phone ? { phone } : {}),
        ...(trade ? { trade } : {}),
        ...(notes ? { notes } : {}),
      };
      if (editingId) {
        const updated = await projectsApi.updateContractor(editingId, payload);
        setContractors((prev) =>
          prev
            .map((c) => (c.id === editingId ? updated : c))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      } else {
        const created = await projectsApi.createContractor(payload);
        setContractors((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      }
      resetForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    await projectsApi.deleteContractor(id);
    setContractors((prev) => prev.filter((c) => c.id !== id));
    setConfirmDeleteId(null);
  }

  return (
    <main className="mx-auto max-w-4xl px-6 py-16">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl font-bold text-phoenix-blue-900">Contractors</h1>
        <button
          type="button"
          onClick={() => (showAdd ? resetForm() : startAdd())}
          className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700"
        >
          {showAdd ? 'Cancel' : '+ Add contractor'}
        </button>
      </div>
      <p className="mt-2 text-sm text-gray-500">
        Reusable across every client and project — pick from this list when assigning a task.
      </p>

      <div className="relative mt-4 max-w-md">
        <svg
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
        </svg>
        <input
          type="search"
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          placeholder="Filter contractors by name, trade, or phone…"
          className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm"
        />
      </div>

      {showAdd && (
        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="mt-6 space-y-3 rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
        >
          <input
            type="text"
            placeholder="Name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            maxLength={200}
            required
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Trade — e.g. Gutters, Painting"
              value={trade}
              onChange={(e) => setTrade(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
            <input
              type="text"
              placeholder="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
          </div>
          <textarea
            placeholder="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            maxLength={4000}
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-phoenix-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Add contractor'}
          </button>
        </form>
      )}

      {state === 'not-configured' && (
        <p className="mt-8 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          The project management backend isn&apos;t connected yet in this environment.
        </p>
      )}
      {state === 'error' && (
        <p className="mt-8 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn&apos;t load contractors. Try again in a moment.
        </p>
      )}
      {state === 'loading' && <p className="mt-8 text-sm text-gray-500">Loading…</p>}

      {state === 'ready' && (
        <div className="mt-6 space-y-2">
          {contractors.length === 0 && (
            <p className="text-sm text-gray-400">No contractors yet — add one above.</p>
          )}
          {contractors.length > 0 && filteredContractors.length === 0 && (
            <p className="text-sm text-gray-400">No contractors match &quot;{filterQuery.trim()}&quot;.</p>
          )}
          {filteredContractors.map((c) => {
            const isExpanded = expandedId === c.id;
            return (
              <div key={c.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2">
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : c.id)}
                      aria-expanded={isExpanded}
                      aria-label={
                        isExpanded ? 'Collapse contractor details' : 'Expand contractor details'
                      }
                      className="mt-0.5 shrink-0 text-gray-400 hover:text-phoenix-blue-600"
                    >
                      <span
                        className={`inline-block transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      >
                        ▶
                      </span>
                    </button>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/admin/contractors/${c.id}`}
                          className="font-semibold text-gray-900 hover:underline"
                        >
                          {c.name}
                          {c.trade && (
                            <span className="ml-2 text-sm font-normal text-gray-500">{c.trade}</span>
                          )}
                        </Link>
                        <CoiStatusBadge contractor={c} />
                        <SoonestLicenseBadge contractor={c} />
                      </div>
                      {c.phone && <p className="text-sm text-gray-500">{c.phone}</p>}
                      {c.notes && <p className="mt-1 text-sm text-gray-600">{c.notes}</p>}
                      <Link
                        to={`/admin/contractors/${c.id}`}
                        className="mt-1 block text-xs text-phoenix-blue-600 hover:underline"
                      >
                        View clients / projects / tasks →
                      </Link>
                    </div>
                  </div>
                  {confirmDeleteId !== c.id ? (
                    editingId === c.id ? null : (
                      <div className="flex shrink-0 items-center gap-3">
                        <button
                          type="button"
                          onClick={() => startEdit(c)}
                          className="text-sm text-gray-600 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(c.id)}
                          className="text-sm text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    )
                  ) : (
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => void handleDelete(c.id)}
                        className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700"
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        className="text-xs text-gray-500 hover:underline"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                {isExpanded && editingId === c.id && (
                  <form
                    onSubmit={(e) => void handleSubmit(e)}
                    className="mt-3 space-y-2 rounded-md border border-gray-200 bg-white p-3"
                  >
                    <input
                      type="text"
                      placeholder="Name *"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                      maxLength={200}
                      required
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Trade — e.g. Gutters, Painting"
                        value={trade}
                        onChange={(e) => setTrade(e.target.value)}
                        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                      />
                    </div>
                    <textarea
                      placeholder="Notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                      maxLength={4000}
                    />
                    {error && <p className="text-sm text-red-600">{error}</p>}
                    <div className="flex items-center gap-3">
                      <button
                        type="submit"
                        disabled={saving}
                        className="rounded-md bg-phoenix-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
                      >
                        {saving ? 'Saving…' : 'Save changes'}
                      </button>
                      <button
                        type="button"
                        onClick={resetForm}
                        className="text-sm text-gray-500 hover:underline"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
                {isExpanded && <ContractorEstimatesSection contractor={c} />}
                {isExpanded && (
                  <ContractorCoiSection
                    contractor={c}
                    onUpdate={(updated) =>
                      setContractors((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
                    }
                  />
                )}
                {isExpanded && (
                  <ContractorLicensesSection
                    contractor={c}
                    onUpdate={(updated) =>
                      setContractors((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
                    }
                  />
                )}
                {isExpanded && (
                  <ContractorPeopleSection
                    contractor={c}
                    onUpdate={(updated) =>
                      setContractors((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}
